import { useEffect, useMemo } from 'react';

import { Form, type FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import type { OptionMetaOption } from './types';
import {
  buildOptionFromSelect,
  buildOptionMetaPatch,
  readOptionMetaByValue,
  readOptionMetaStore,
  readString,
  resolveOptionMetaLookupKey,
} from './utils';

export interface OptionMetaListItem {
  value?: string;
  label?: string;
  extra?: unknown;
}

export interface SyncOptionMetaFieldConfig {
  /** 表单字段路径 */
  field: string;
  /** 下拉原始列表（用于编辑回显 / 异步 options 就绪后补全 name） */
  listItems?: OptionMetaListItem[];
  resolveLookupKey?: (fieldValue: unknown) => string | undefined;
  buildOption?: (fieldValue: unknown, listItems?: OptionMetaListItem[]) => OptionMetaOption | undefined;
  /** 已有 name/label 时跳过，默认 true */
  skipIfNamed?: boolean;
  /** 固定的 lookupKey，传入后直接使用该 key 存储和读取，不再从字段值解析 */
  lookupKey?: string;
}

function hasNamedOption(store: ReturnType<typeof readOptionMetaStore>, lookupKey: string): boolean {
  const existing = readOptionMetaByValue(store, lookupKey);
  return Boolean(readString(existing?.name ?? existing?.label));
}

function findListItem(listItems: OptionMetaListItem[] | undefined, lookupKey: string): OptionMetaListItem | undefined {
  return listItems?.find((item) => item.value === lookupKey);
}

/** 按字段当前值补全 optionMeta（编辑初始化 / options 异步加载后调用） */
function syncOptionMetaForField(form: FormInstance, config: SyncOptionMetaFieldConfig): void {
  const { field, listItems, buildOption, resolveLookupKey = resolveOptionMetaLookupKey, skipIfNamed = true, lookupKey: fixedKey } = config;
  const formValues = form.getFieldsValue() as Record<string, unknown>;
  const fieldValue = get(formValues, field);
  const lookupKey = fixedKey ?? resolveLookupKey(fieldValue);
  if (!lookupKey) return;

  const store = readOptionMetaStore(formValues);
  if (skipIfNamed && hasNamedOption(store, lookupKey)) return;

  let option = buildOption?.(fieldValue, listItems);
  if (!option) {
    const listItem = findListItem(listItems, lookupKey);
    option = buildOptionFromSelect(lookupKey, listItem ? { label: listItem.label, extra: listItem.extra } : undefined, listItems);
  }
  if (!readString(option?.name ?? option?.label)) return;

  form.setFieldsValue(buildOptionMetaPatch(lookupKey, option, formValues, fixedKey));
}

function useListItemsVersion(listItems?: OptionMetaListItem[]): string {
  return useMemo(() => listItems?.map((item) => item.value).join('\0') ?? '', [listItems]);
}

/** 监听字段值 + options，有值且缺 name 时自动写入 optionMeta */
export function useSyncOptionMetaField(form: FormInstance, config: SyncOptionMetaFieldConfig & { enabled?: boolean }): void {
  const { enabled = true, field, listItems, buildOption, resolveLookupKey, skipIfNamed, lookupKey } = config;
  const fieldValue = Form.useWatch(field, form);
  const listVersion = useListItemsVersion(listItems);

  useEffect(() => {
    if (!enabled) return;
    syncOptionMetaForField(form, { field, listItems, buildOption, resolveLookupKey, skipIfNamed, lookupKey });
  }, [enabled, field, fieldValue, listVersion, form, buildOption, resolveLookupKey, skipIfNamed, lookupKey]);
}
